import React, { useState, useEffect, useContext, createContext } from 'react';
import { getUserCountryCode } from '../utils/getusercountrycode';
import { CREATE_CART_QUERY, FETCH_CART_QUERY, MODIFY_CART_MUTATIONS, APPLY_CART_DISCOUNT_CODES } from '../queries/shopifyQueries';
import { shopifyMarketCountries } from '../utils/countries';

const SHOPIFY_API_ENDPOINT = `https://${process.env.GATSBY_SHOPIFY_STORE_URL}/api/2024-04/graphql.json`;
const SHOPIFY_ACCESS_TOKEN = process.env.GATSBY_SHOPIFY_STORE_FRONT_ACCESS_TOKEN;
const SHOPIFY_CART_STORAGE_KEY = 'shopify_cart_id';

const StoreContext = createContext({});

export const StoreContextProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    const initializeCart = async () => {
      const storedCartId = typeof window !== 'undefined' ? localStorage.getItem(SHOPIFY_CART_STORAGE_KEY) : null;
      const countryCode = await getUserCountryCode();

      if (storedCartId && countryCode) {
        const cart = await fetchCart(storedCartId, countryCode);

        if (cart) {
          const expectedCurrency = shopifyMarketCountries.find(country => country.code === countryCode)?.currency

          const cartCurrency = cart.cost.totalAmount.currencyCode;

          if (cartCurrency === expectedCurrency) {
            setCart(cart);
            return;
          } else {
            const lineItems = cart.lines.edges.map(edge => ({
              merchandiseId: edge.node.merchandise.id,
              quantity: edge.node.quantity,
              attributes: edge.node.attributes,
            }));

            const newCart = await createCart(lineItems, countryCode);

            if (newCart) {
              localStorage.setItem(SHOPIFY_CART_STORAGE_KEY, newCart.id);
              setCart(newCart);
            }
            return;
          }
        } else {
          localStorage.removeItem(SHOPIFY_CART_STORAGE_KEY);
        }
      }

      const newCart = await createCart([], countryCode);
      if (newCart) {
        localStorage.setItem(SHOPIFY_CART_STORAGE_KEY, newCart.id);
        setCart(newCart);
      }
    };

    initializeCart();
  }, []);

  return (
    <StoreContext.Provider
      value={{
        cart,
        isAdding,
        setIsAdding,
        setCart,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export const useStoreContext = () => useContext(StoreContext);

// ============ Shopify Storefront API Calls ============

const shopifyFetch = async (query, variables, countryCode) => {

  const res = await fetch(SHOPIFY_API_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': SHOPIFY_ACCESS_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });


  const json = await res.json();
  if (json.errors) {
    console.error('Shopify Storefront API errors:', json.errors);
  }
  return json.data;
};

const createCart = async (lines = [], country = 'US') => {
  const data = await shopifyFetch(CREATE_CART_QUERY, { lines, country }, country);
  return data?.cartCreate?.cart || null;
};

const fetchCart = async (cartId, country) => {
  const data = await shopifyFetch(FETCH_CART_QUERY, { cartId, country }, country);
  return data?.cart || null;
};

const modifyCart = async (operation, variables, country) => {
  const data = await shopifyFetch(MODIFY_CART_MUTATIONS[operation], variables, country);
  const cartResponseKey = Object.keys(data)[0];
  return data[cartResponseKey]?.cart || null;
};

// ============ Hooks ============

export const useAddItemToCart = () => {
  const { cart, setCart, setIsAdding } = useStoreContext();

  return async ({ variantId, quantity, attributes = [] }) => {
    setIsAdding(true);
    const country = await getUserCountryCode();

    const newCart = await modifyCart(
      'add',
      {
        cartId: cart.id,
        lines: [
          {
            merchandiseId: variantId,
            quantity: parseInt(quantity, 10),
            attributes,
            // attributes: attributes.reduce((acc, attr) => {
            //   acc[attr.key] = attr.value;
            //   return acc;
            // }, {}),
          },
        ],
        country,
      },
      country
    );

    if (newCart) {
      setCart(newCart);
      return true;
    }
    setIsAdding(false);
    return false;
  };
};

export const useRemoveItemFromCart = () => {
  const { cart, setCart } = useStoreContext();
  return async (lineId) => {
    const country = await getUserCountryCode();
    const newCart = await modifyCart(
      'remove',
      {
        cartId: cart.id,
        lineIds: [lineId],
        country,
      },
      country
    );
    if (newCart) setCart(newCart);
  };
};

export const useUpdateItemQuantity = () => {
  const { cart, setCart } = useStoreContext();
  return async (lineId, quantity) => {
    const country = await getUserCountryCode();
    const newCart = await modifyCart(
      'update',
      {
        cartId: cart.id,
        lines: [
          {
            id: lineId,
            quantity: parseInt(quantity, 10),
          },
        ],
        country,
      },
      country
    );
    if (newCart) setCart(newCart);
  };
};

export const useCartItems = () => {
  const { cart } = useStoreContext();
  return cart?.lines?.edges?.map((edge) => edge.node) || [];
};

export const useCartCount = () => {
  const { cart } = useStoreContext();
  return cart?.totalQuantity || 0;
};

export const useCartTotals = () => {
  const { cart } = useStoreContext();
  return cart?.cost?.totalAmount?.amount || '0.00';
};

export const useCheckout = () => {
  const { cart } = useStoreContext();
  return cart?.checkoutUrl || '';
};

export const useApplyCartDiscountCodes = () => {
  const { cart, setCart } = useStoreContext();

  return async (discountCodes) => {
    if (!cart?.id || !discountCodes?.length) {
      return { cart, errors: [] };
    }

    const country = await getUserCountryCode();
    const data = await shopifyFetch(
      APPLY_CART_DISCOUNT_CODES,
      { cartId: cart.id, discountCodes, country },
      country
    );

    const result = data?.cartDiscountCodesUpdate;
    const errors = result?.userErrors || [];

    if (errors.length) {
      console.error('cartDiscountCodesUpdate errors:', errors);
      return { cart: null, errors };
    }

    if (result?.cart) {
      setCart(result.cart);
      return { cart: result.cart, errors: [] };
    }

    return { cart: null, errors: [{ message: 'Could not apply discount code.' }] };
  };
};
